<script setup lang="tsx">
import { computed, onMounted, ref } from 'vue';
import type { DataTableColumns } from 'naive-ui';
import { fetchGetNotifications } from '@/service/api';
import { useRouterPush } from '@/hooks/common/router';
import { useAuthStore } from '@/store/modules/auth';

defineOptions({ name: 'SubscriptionNotificationModal' });

const authStore = useAuthStore();
const { routerPushByKey } = useRouterPush(false);
const visible = ref(false);
const loading = ref(false);
const notifications = ref<Api.Auth.Notifications>({
  metricsAlerts: [],
  applicationAlerts: { googlePlay: [], appStore: [] }
});

const metricsCount = computed(() => notifications.value.metricsAlerts.length);
const applicationAlerts = computed(() => [
  ...notifications.value.applicationAlerts.googlePlay.map(alert => ({ ...alert, storeType: 'googlePlay' as const })),
  ...notifications.value.applicationAlerts.appStore.map(alert => ({ ...alert, storeType: 'appStore' as const }))
]);
const applicationCount = computed(() => applicationAlerts.value.length);

// 新进应用尚未配置应用名，仅有包名，展示和排序时回退到包名
function getAppLabel(row: Api.Auth.ApplicationAlertWithStoreType) {
  return row.appName?.trim() || row.packageName;
}

const sortedApplicationAlerts = computed(() =>
  [...applicationAlerts.value].sort((left, right) => getAppLabel(left).localeCompare(getAppLabel(right)))
);

const levelText: Record<number, string> = { 1: '🟡 提示', 2: '🟠 警告', 3: '🔴 严重' };

const metricsColumns: DataTableColumns<Api.Auth.MetricsAlert> = [
  { title: '级别', key: 'level', width: 64, render: row => levelText[row.level] ?? String(row.level) },
  {
    title: '超出倍数',
    key: 'breachMultiple',
    width: 72,
    render: row =>
      row.breachMultiple === null || row.breachMultiple === undefined
        ? '-'
        : `${Number(row.breachMultiple.toFixed(1))}×`
  },
  { title: '应用', key: 'appName', width: 105, ellipsis: { tooltip: true }, render: row => row.appName?.trim() || '-' },
  { title: '告警类型', key: 'alertType', width: 160, render: row => row.alertType || '-' },
  {
    title: '类型',
    key: 'type',
    width: 95,
    render: row => row.type || '-'
  },
  {
    title: '说明',
    key: 'detail',
    minWidth: 280,
    render: row => row.detail || '-'
  }
];

const applicationColumns: DataTableColumns<Api.Auth.ApplicationAlertWithStoreType> = [
  {
    title: '商店类型',
    key: 'storeType',
    width: 100,
    render: row => (
      <NTag type={row.storeType === 'appStore' ? 'info' : 'success'} size="small" bordered={false} round>
        {row.storeType === 'appStore' ? 'App Store' : 'Google Play'}
      </NTag>
    )
  },
  { title: '应用名', key: 'appName', width: 130, ellipsis: { tooltip: true }, render: row => getAppLabel(row) },
  { title: '包名', key: 'packageName', width: 190, ellipsis: { tooltip: true } },
  { title: '商店名称', key: 'storeName', width: 130, ellipsis: { tooltip: true }, render: row => row.storeName || '-' },
  {
    title: '运营账号',
    key: 'operationAccount',
    width: 170,
    ellipsis: { tooltip: true },
    render: row => row.operationAccount || '-'
  },
  { title: '告警状态', key: 'alarmStatus', width: 100 },
  { title: '下架日期', key: 'removalTime', width: 100, render: row => row.removalTime || '-' }
];

async function loadNotifications() {
  const userInfoReady = await authStore.refreshUserInfo();
  if (!userInfoReady) return;
  // 未配置订阅信息的旧用户默认开启，仅显式关闭时不拉取通知。
  if (authStore.userInfo.subscriptionInfo?.enabled === false) return;

  loading.value = true;
  try {
    const { data, error } = await fetchGetNotifications();
    if (error || !data) return;
    notifications.value = {
      metricsAlerts: data.metricsAlerts ?? [],
      applicationAlerts: {
        googlePlay: data.applicationAlerts?.googlePlay ?? [],
        appStore: data.applicationAlerts?.appStore ?? []
      }
    };
    visible.value = metricsCount.value > 0 || applicationCount.value > 0;
  } finally {
    loading.value = false;
  }
}

function ignore() {
  visible.value = false;
}

function goToReportMonitor() {
  visible.value = false;
  routerPushByKey('report-monitor');
}

onMounted(loadNotifications);
</script>

<template>
  <NModal
    v-model:show="visible"
    preset="card"
    title="消息通知"
    style="width: min(96vw, 1200px)"
    :closable="false"
    :mask-closable="false"
    :close-on-esc="false"
    class="notification-modal"
    :content-style="{ maxHeight: '68vh', overflowY: 'auto' }"
  >
    <div class="notification-lists">
      <section v-if="applicationCount > 0" class="notification-section">
        <h3 class="notification-section__alert-title">
          <span class="notification-section__alert-icon">
            <SvgIcon icon="material-symbols:error-rounded" />
          </span>
          <span>应用状态异常详情</span>
        </h3>
        <NDataTable
          :columns="applicationColumns"
          :data="sortedApplicationAlerts"
          :pagination="false"
          :loading="loading"
          :scroll-x="920"
          size="small"
          striped
        />
      </section>

      <section class="notification-section">
        <h3>指标告警</h3>
        <NDataTable
          :columns="metricsColumns"
          :data="notifications.metricsAlerts"
          :pagination="false"
          :loading="loading"
          :scroll-x="900"
          size="small"
          striped
        />
      </section>
    </div>

    <template #footer>
      <div class="notification-modal__footer">
        <NButton @click="ignore">忽略</NButton>
        <NButton type="primary" @click="goToReportMonitor">前往报表监控</NButton>
      </div>
    </template>
  </NModal>
</template>

<style scoped>
.notification-lists {
  display: grid;
  gap: 24px;
}

.notification-section h3 {
  margin: 0 0 8px;
  font-size: 16px;
}

.notification-section__alert-title {
  display: flex;
  align-items: center;
  gap: 6px;
}

.notification-section__alert-icon {
  display: inline-flex;
  color: #d03050;
  font-size: 18px;
}

.notification-modal__footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}
</style>
